import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Percent, Plus, Pencil } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Button, Chip, Skeleton, EmptyState, Toast } from "@/ui";

function peso(c) {
  const w = Math.floor((c || 0) / 100);
  const f = String(Math.abs(c || 0) % 100).padStart(2, "0");
  return `\u20b1${w.toLocaleString()}.${f}`;
}

export function triggerWords(t) {
  if (!t) return "On any order";
  switch (t.type) {
    case "specific_products": return `On chosen items (${(t.productIds || []).length})`;
    case "minimum_spend": return `When they spend ${peso(t.minSpendCentavos)} or more`;
    case "first_time_customer": return "For first-time customers";
    case "specific_channel": return `On the ${(t.channel || "").replace("_", " ")}`;
    default: return "On any order";
  }
}

export function rewardWords(r) {
  if (!r) return "";
  switch (r.type) {
    case "percent_off": return `${r.value}% off`;
    case "amount_off": return `${peso(r.value)} off`;
    case "free_item": return "A free item";
    case "cheapest_free": return "Cheapest item free";
    case "free_upgrade": return "A free upgrade";
    case "bundle_price": return `Bundle at ${peso(r.value)}`;
    default: return "";
  }
}

function whenWords(w) {
  if (!w || (!w.startsAt && !w.endsAt)) return "Runs anytime";
  const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-PH", { month: "short", day: "numeric" }) : "");
  if (w.startsAt && w.endsAt) return `${fmt(w.startsAt)} \u2013 ${fmt(w.endsAt)}`;
  if (w.endsAt) return `Until ${fmt(w.endsAt)}`;
  return `From ${fmt(w.startsAt)}`;
}

export default function Deals() {
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = () => {
    if (!shopId) return;
    api.get(`/deals?shopId=${shopId}`).then((r) => setDeals(r.data.deals)).catch((e) => setError(friendlyError(e)));
  };
  useEffect(load, [shopId]);

  const toggle = async (d) => {
    try {
      await api.post(`/deals/${d.id}/toggle?shopId=${shopId}&active=${!d.active}`);
      setToast(!d.active ? `"${d.name}" is now on` : `"${d.name}" is off`);
      setTimeout(() => setToast(""), 2500);
      load();
    } catch (e) { setError(friendlyError(e)); }
  };

  return (
    <Layout shopName={shopName}>
      <div className="row-between" style={{ marginBottom: 20, alignItems: "flex-start" }}>
        <div>
          <h1 className="t-h1">Deals</h1>
          <p className="muted">Every deal is the same four questions. No jargon.</p>
        </div>
        <Button variant="primary" data-testid="new-deal-btn" onClick={() => navigate("/deals/new")}>
          <Plus size={16} strokeWidth={1.75} />new deal
        </Button>
      </div>

      {error ? <Card><p className="err-text" data-testid="deals-error">{error}</p></Card> : null}

      {deals === null ? (
        <div className="grid grid-2" style={{ gap: 16 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={120} />)}</div>
      ) : deals.length === 0 ? (
        <EmptyState icon={Percent} title="No deals yet" hint="Make your first one in four quick questions.">
          <Button variant="primary" onClick={() => navigate("/deals/new")}>make a deal</Button>
        </EmptyState>
      ) : (
        <div className="grid grid-2" style={{ gap: 16 }}>
          {deals.map((d) => (
            <Card key={d.id} data-testid={`deal-${d.id}`}>
              <div className="row-between" style={{ marginBottom: 10 }}>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <h3 className="t-h3">{d.name}</h3>
                  <Chip tone={d.active ? "good" : "neutral"} dot>{d.active ? "on" : "off"}</Chip>
                  {d.stackable ? <Chip tone="neutral">stacks</Chip> : null}
                </div>
                <Button size="sm" data-testid={`deal-toggle-${d.id}`} onClick={() => toggle(d)}>{d.active ? "turn off" : "turn on"}</Button>
              </div>
              <p className="t-body" style={{ marginBottom: 4 }}><strong>{rewardWords(d.reward)}</strong></p>
              <p className="muted t-small">{triggerWords(d.trigger)}{" \u00b7 "}{whenWords(d.when)}</p>
              <div className="row-between" style={{ marginTop: 14, alignItems: "center" }}>
                <span className="muted-3 t-small" data-testid={`deal-perf-${d.id}`}>
                  {d.usageCount} used {"\u00b7"} {d.pesosGiven} given
                </span>
                <Button size="sm" data-testid={`deal-edit-${d.id}`} onClick={() => navigate(`/deals/${d.id}/edit`)}>
                  <Pencil size={14} strokeWidth={1.75} />edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="deals-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
